import React, { useEffect, useState, useCallback, Fragment } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { observer } from 'mobx-react';
import classNames from 'classnames';

import { translate } from 'services/i18nService';
import ChatStore from 'stores/ChatStore';
import FirebaseStore from 'stores/FirebaseStore';
import GamesStore from 'stores/GamesStore';
import PaymentStore from 'stores/PaymentStore';
import BalanceInfoMobile from 'components/BalanceInfoMobile/BalanceInfoMobile';
import Button from 'components/Button/Button';
import ErrorAlert from 'components/ErrorAlert/ErrorAlert';
import LoadingButton from 'components/LoadingButton/LoadingButton';
import { SECOND_AS_MILLISECONDS } from 'enum/timeFormat';
import { GAMES, GAMES_STATUS } from 'utils/consts';
import ThemeService from 'themes/ThemeService';

import dice1 from '../../../images/games/dice1.png';
import dice2 from '../../../images/games/dice2.png';
import dice3 from '../../../images/games/dice3.png';
import dice4 from '../../../images/games/dice4.png';
import dice5 from '../../../images/games/dice5.png';
import dice6 from '../../../images/games/dice6.png';
import iconTokens from '../../../images/icontokens.png';

import styles from './RollTheDice.css';


function RollTheDice(props) {
  const {
    embedProfileV2,
    isDesktop,
    roomName,
    setShowRegistrationModal,
    showBuyTokensModal,
    toggleGameDrawer,
    username,
  } = props;

  const {
    clearResult,
    isPlayActionLoading,
    pauseTime,
    price,
    prizes,
    result,
    rollTheDiceClick,
    status,
  } = GamesStore;
  const { apiToken } = FirebaseStore;
  const { balance } = PaymentStore;

  const [isPlayed, setIsPlayed] = useState(false);
  const [timer, setTimer] = useState(null);
  const [diceResult, setDiceResult] = useState(null);
  const [showNoFunds, setShowNoFunds] = useState(false);

  const diceIcons = [dice1, dice2, dice3, dice4, dice5, dice6];
  const noFunds = parseInt(balance, 10) < parseInt(price, 10) || !username;
  const RESULT_DELAY = 2000;
  const CLOSE_DELAY = 4000;
  const isButtonDisabled = isPlayed || status === GAMES_STATUS.PAUSE;
  const showNoFundsOverlay = (noFunds && !isDesktop) || (showNoFunds && noFunds);

  const handleDrawer = useCallback(() => {
    if (!isDesktop) {
      toggleGameDrawer();
    } else {
      setDiceResult(null);
      clearResult();
      ChatStore.setProfileChatTabValue(0);
    }
  }, [isDesktop, toggleGameDrawer, clearResult]);

  useEffect(() => {
    if (apiToken) {
      const data = {
        game: GAMES.ROLL_THE_DICE,
        roomName,
        token: apiToken,
      };
      GamesStore.getGameInfo(data);
    }
  }, [roomName, apiToken]);

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (pauseTime && status === GAMES_STATUS.PAUSE) {
      const countdown = setInterval(() => {
        const timeLeft = moment(pauseTime).diff(moment());
        const formatted = moment(timeLeft).format('mm:ss');
        if (timeLeft < 0) {
          setTimer(null);
          clearInterval(countdown);
        } else {
          setTimer(formatted);
        }
      }, SECOND_AS_MILLISECONDS);

      return () => {
        clearInterval(countdown);
      };
    }
    setTimer(null);
  }, [pauseTime, status]);

  useEffect(() => {
    let resultTimeout = null;
    let closeTimeout = null;
    if (result && isPlayed) {
      resultTimeout = setTimeout(() => {
        setDiceResult(result);
      }, RESULT_DELAY);
      closeTimeout = setTimeout(() => {
        setIsPlayed(false);
        handleDrawer();
      }, CLOSE_DELAY);
    }

    return () => {
      clearTimeout(resultTimeout);
      clearTimeout(closeTimeout);
    };
  }, [result, handleDrawer, isPlayed]);

  const onClick = useCallback(() => {
    if (noFunds) {
      setShowNoFunds(true);
    } else {
      setIsPlayed(true);
      rollTheDiceClick(roomName, apiToken);
    }
  }, [
    apiToken,
    noFunds,
    rollTheDiceClick,
    roomName,
  ]);

  const onShowBuyTokensModal = useCallback(() => {
    if (embedProfileV2 && !username) {
      setShowRegistrationModal(true);
    } else {
      showBuyTokensModal();
    }
  }, [embedProfileV2, showBuyTokensModal, setShowRegistrationModal, username]);

  const buyTokens = useCallback(() => {
    handleDrawer();
    onShowBuyTokensModal();
  }, [handleDrawer, onShowBuyTokensModal]);

  const dotRenderer = (times) => [...Array(times)].map((_, i) => <span key={i} className={styles.dot} />);

  const renderButton = (
    <Fragment>
      {timer ? (
        <span className={styles.timer}>{timer}</span>
      ) : (
        <div className={styles.price}>
          <img alt="tokens" src={iconTokens} />
          {translate('v2profile.tokens.amount', { 0: price })}
        </div>
      )}
      <LoadingButton
        className={styles.btn}
        colour="orange"
        disabled={isButtonDisabled}
        id={`rollTheDiceButton_${isDesktop ? 'desktop' : 'mobile'}`}
        isLoading={isPlayActionLoading}
        onClick={onClick}
      >
        {translate('v2profile.games.play')}
      </LoadingButton>
    </Fragment>
  );

  const diceTheme = ThemeService.theme.games.dice;

  const rollTheDiceContainerClasses = classNames(
    styles.rollTheDiceContainer,
    diceTheme.containerClass,
    { [styles.containerDesktop]: isDesktop },
  );

  const prizesClasses = classNames(styles.prizes, { [styles.prizesDesktop]: isDesktop });

  const prizeClasses = classNames(styles.prize, { [styles.prizeDesktop]: isDesktop });

  const diceWrapperClasses = classNames(styles.diceWrapper, { [styles.diceWrapperDesktop]: isDesktop });

  return (
    <div className={styles.container}>
      <div className={rollTheDiceContainerClasses}>
        {isDesktop && (
          <div className={styles.buttonContainer}>
            {renderButton}
          </div>
        )}
        <ul className={prizesClasses}>
          {prizes &&
          Object.values(prizes).map((el, index) => (
            <li key={`${el}${index}`} className={prizeClasses}>
              <img alt="dice" src={diceIcons[index]} />
              <span className={`${diceResult === index + 1 && isPlayed ? styles.blink : ''}`}>{el}</span>
            </li>
          ))}
        </ul>

        <div className={styles.action}>
          <div className={diceWrapperClasses}>
            <div className={isDesktop ? styles.diceDesktop : styles.dice}>
              <ol className={`${styles.dieList} ${result ? styles.oddRoll : ''}`} data-roll={result}>
                <li className={styles.dieItem} data-side="1">
                  {dotRenderer(1)}
                </li>
                <li className={styles.dieItem} data-side="2">
                  {dotRenderer(2)}
                </li>
                <li className={styles.dieItem} data-side="3">
                  {dotRenderer(3)}
                </li>
                <li className={styles.dieItem} data-side="4">
                  {dotRenderer(4)}
                </li>
                <li className={styles.dieItem} data-side="5">
                  {dotRenderer(5)}
                </li>
                <li className={styles.dieItem} data-side="6">
                  {dotRenderer(6)}
                </li>
              </ol>
            </div>
          </div>
          {!isDesktop && renderButton}
        </div>
      </div>

      {!isDesktop && (
        <BalanceInfoMobile
          areTokensNeeded={balance < price}
          id="rollTheDice_buyTokens_mobile"
          onClick={buyTokens}
        />
      )}

      {(showNoFundsOverlay && isDesktop) && (
        <div className={styles.noFunds}>
          <ErrorAlert message={translate('v2profile.insufficient.balance')} />
          <Button
            colour="green"
            id={`rollTheDice_buyTokens_${isDesktop ? 'desktop' : 'mobile'}`}
            label={translate('v2profile.buy.tokens')}
            onClick={buyTokens}
          />
        </div>
      )}
    </div>
  );
}

RollTheDice.propTypes = {
  roomName: PropTypes.string.isRequired,
  showBuyTokensModal: PropTypes.func.isRequired,
  username: PropTypes.string.isRequired,
  embedProfileV2: PropTypes.bool,
  isDesktop: PropTypes.bool,
  setShowRegistrationModal: PropTypes.func,
  toggleGameDrawer: PropTypes.func,
};

RollTheDice.defaultProps = {
  embedProfileV2: false,
  isDesktop: false,
  setShowRegistrationModal: () => undefined,
  toggleGameDrawer: () => undefined,
};

export default observer(RollTheDice);
